import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { TableCard } from "../shared";
import { onPremLowestComplianceColumnDefs, cloudLowestComplianceColumnDefs } from "../col_defs";
import useDashboardService from "../services";
import { toast } from "react-toastify";
import eventEmitter from "../../../components/utils/eventEmitter";
import { ComplianceCellRenderer } from "../cell_renderers";
import AssetDetailsDrawer from "../../../components/asset/AssetDetailsDrawer";
import {
  SEVERITY_FILTER_OPTIONS as filterOptions,
  DEFAULT_SEVERITY_FILTER as defaultFilter,
} from "../shared/constants";
import AntSwitch from "../../../components/AntSwitch";
import { setSelectedRowID } from "../../../pages/SecurityPosture/TabSelection-slice";
import { useDispatch } from "react-redux";
import { Drawer, Box, CircularProgress } from "@mui/material";
import AssetComplianceReportDrawer from "../../../components/asset/AssetComplianceReportDrawer";
import { useDashboardContext } from "../DashboardContext";

const PAGE_SIZE = 10;
const SCROLL_THROTTLE_MS = 500;

interface TopMisconfigurationProps {
  isbothCloudAndPremAssetUser: boolean;
  isOnlyPremAssetUser: boolean;
  isOnlyCloudUser: boolean;
}

const OnPremLowestCompliance = ({ isbothCloudAndPremAssetUser: _isbothCloudAndPremAssetUser, isOnlyPremAssetUser: _isOnlyPremAssetUser, isOnlyCloudUser: _isOnlyCloudUser }: TopMisconfigurationProps) => {
  const { fetchAssetsCompliance } = useDashboardService();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [newColumnDefs, setNewColumnDefs] = useState<any>([]);
  const [nistColumns, setNistColumns] = useState<any>([]);
  const [cisColumns, setCisColumns] = useState<any>([]);
  const [pciDssColumns, setPciDssColumns] = useState<any>([]);
  const [sparkColumns, setSparkColumns] = useState<any>([]);
  const [nistColumnsDefs, setNistColumnsDefs] = useState<any>([]);
  const [cisColumnsDefs, setCisColumnsDefs] = useState<any>([]);
  const [pciDssColumnsDefs, setPciDssColumnsDefs] = useState<any>([]);
  const [sparkColumnsDefs, setSparkColumnsDefs] = useState<any>([]);
  // Track currently selected columns to maintain state when reopening filter
  const [currentSelectedNist, setCurrentSelectedNist] = useState<string>('');
  const [currentSelectedCis, setCurrentSelectedCis] = useState<string>('');
  const [currentSelectedPciDss, setCurrentSelectedPciDss] = useState<string>('');
  const [currentSelectedSpark, setCurrentSelectedSpark] = useState<string>('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [switchDisabled, setSwitchDisabled] = useState(false);
  const [isCloud, setIsCloud] = useState(false);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });
  
  const gridRef = useRef<any>(null);
  const isFetchingNext = useRef(false);
  const isFetchingPrev = useRef(false);
  const isCloudRef = useRef<boolean>(isCloud);
  const lastScrollTs = useRef<number>(0);
  const dispatch = useDispatch();
  const firstVisibleRowRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);
  const scrollCount = useRef(0); // Count scroll events

  // Update ref when isCloud changes
  useEffect(() => {
    isCloudRef.current = isCloud;
  }, [isCloud]);

  const { setIsOverlayOpen } = useDashboardContext();

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(isDrawerOpen || reportDrawerOpen);
  }, [isDrawerOpen, reportDrawerOpen, setIsOverlayOpen]);
  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger(prev => prev + 1);
    };
    eventEmitter.on('filterSaved', handleFilterSaved);
    return () => {
      eventEmitter.off('filterSaved', handleFilterSaved);
    };
  }, []);

  useEffect(() => {
    if (_isOnlyCloudUser) {
      setIsCloud(true);
      setSwitchDisabled(true);
    } else if (_isOnlyPremAssetUser) {
      setIsCloud(false);
      setSwitchDisabled(true);
    } else {
      setSwitchDisabled(false);
    }
  }, [_isOnlyCloudUser, _isOnlyPremAssetUser]);


  // Initial fetch - FIXED: removed fetchData from dependencies
  useEffect(() => {
    // Sync ref so fetchData can read latest value
    isCloudRef.current = isCloud;
    scrollCount.current = 0; // Reset scroll counter
    isFetchingNext.current = false; // Reset fetching flags
    isFetchingPrev.current = false;

    setTableData([]);
    setPagination({
      page: 1,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    });

    // Call fetchData directly without adding it to deps
    fetchData({ page: 1, mode: "initial" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, isCloud]);

  const generateNISTColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const nistKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("NIST_")
    );
    setNistColumns(nistKeys);
    return nistKeys.map((key) => ({
      field: key,
      headerName: key,
      filter: false,
      sortable: true,
      // sort: "asc",
      flex: 2.5,
      cellRenderer: ComplianceCellRenderer,
      valueGetter: (params: any) => params.data[key] || 0
    }));
  };

  const generateCisColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const cisKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("CIS_")
    );
    setCisColumns(cisKeys);
    return cisKeys.map((key) => ({
      field: key,
      headerName: key,
      filter: false,
      sortable: true,
      // sort: "asc",
      flex: 2.5,
      cellRenderer: ComplianceCellRenderer,
      valueGetter: (params: any) => params.data[key] || 0
    }));
  };

  const generatePciDssColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const pciDssKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("PCI DSS_")
    );
    setPciDssColumns(pciDssKeys);
    return pciDssKeys.map((key) => ({
      field: key,
      headerName: key,
      filter: false,
      sortable: true,
      // sort: "asc",
      flex: 2.5,
      cellRenderer: ComplianceCellRenderer,
      valueGetter: (params: any) => params.data[key] || 0
    }));
  };

  const generateSparkColumns = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const sparkKeys = Object.keys(data[0]).filter((key) =>
      key.startsWith("SPARK_")
    );
    setSparkColumns(sparkKeys);
    return sparkKeys.map((key) => ({
      field: key,
      headerName: key,
      filter: false,
      sortable: true,
      // sort: "asc",
      flex: 2.5,
      cellRenderer: ComplianceCellRenderer,
      valueGetter: (params: any) => params.data[key] || 0
    }));
  };

  const fetchData = useCallback(async ({
    page = 1,
    mode = "initial",
  }: {
    page?: number;
    mode?: "initial" | "append" | "prepend";
  } = {}) => {
    const currentIsCloud = isCloudRef.current;
    
    // Capture scroll state before fetching
    try {
      const apiBefore = gridRef.current?.api;
      if (apiBefore && mode !== "initial") {
        firstVisibleRowRef.current = typeof apiBefore.getFirstDisplayedRow === "function" ? apiBefore.getFirstDisplayedRow() : firstVisibleRowRef.current;
        const vRangeBefore = apiBefore.getVerticalPixelRange?.();
        scrollPositionRef.current = vRangeBefore?.top ?? scrollPositionRef.current;
      }
    } catch (err) {
      console.warn("Could not capture pre-fetch scroll state for OnPremLowestCompliance", err);
    }

    if (mode === "initial") {
      setLoading(true);
    } else {
      setIsFetchingMore(true);
    }

    try {
      const module = currentIsCloud ? "cloud" : "on_premise";
      const baseColumnDefs = currentIsCloud ? cloudLowestComplianceColumnDefs : onPremLowestComplianceColumnDefs;

      const {
        data,
        pagination: pageInfo,
        error,
      } = await fetchAssetsCompliance(module, {
        page,
        page_size: PAGE_SIZE,
      });

      console.log("📥 API Response:", {
        module,
        page,
        dataType: Array.isArray(data) ? "array" : typeof data,
        dataLength: Array.isArray(data) ? data.length : "N/A",
        dataSample: Array.isArray(data) && data.length > 0 ? data[0] : data,
        pageInfo,
        error,
      });

      if (error) {
        toast.error((error as any)?.message || "Failed to load assets compliance data");
        return;
      }

      setPagination({
        page: pageInfo?.page || page,
        totalPages: pageInfo?.total_pages || 1,
        hasNext: pageInfo?.has_next || false,
        hasPrevious: pageInfo?.has_previous || false,
      });

      // Update tableData based on mode
      setTableData((prev: any) => {
        if (mode === "initial") {
          console.log("✅ Setting initial data:", data?.length ?? 0, "items");
          return data || [];
        }

        const itemsToAdd = data || [];

        if (mode === "append") {
          console.log(
            "➕ Appending:",
            itemsToAdd.length,
            "items to",
            prev.length,
            "existing"
          );
          return [...prev, ...itemsToAdd];
        }
        if (mode === "prepend") {
          console.log(
            "⬆️ Prepending:",
            itemsToAdd.length,
            "items to",
            prev.length,
            "existing"
          );
          return [...itemsToAdd, ...prev];
        }

        return prev;
      });

      // Only generate column definitions on initial load, not on append/prepend
      // This prevents header collision during infinite scroll
      if (mode === "initial" && data && data.length > 0) {
        const nistCols = generateNISTColumns(data);
        const cisCols = generateCisColumns(data);
        const pciDssCols = generatePciDssColumns(data);
        const sparkCols = generateSparkColumns(data);
        const singleNistColumn = nistCols[0];
        const singleCisColumn = cisCols[0];
        const singlePciDssColumn = pciDssCols[0];
        const singleSparkColumn = sparkCols[0];
        setNistColumnsDefs(nistCols);
        setCisColumnsDefs(cisCols);
        setPciDssColumnsDefs(pciDssCols);
        setSparkColumnsDefs(sparkCols);
        // Set initial selected column values
        setCurrentSelectedNist(singleNistColumn?.field || '');
        setCurrentSelectedCis(singleCisColumn?.field || '');
        setCurrentSelectedPciDss(singlePciDssColumn?.field || '');
        setCurrentSelectedSpark(singleSparkColumn?.field || '');
        setNewColumnDefs([...baseColumnDefs, singleNistColumn, singleCisColumn, singlePciDssColumn, ...(singleSparkColumn ? [singleSparkColumn] : [])]);
      } else if (mode === "initial") {
        setNistColumns([]);
        setCisColumns([]);
        setPciDssColumns([]);
        setSparkColumns([]);
        setNistColumnsDefs([]);
        setCisColumnsDefs([]);
        setPciDssColumnsDefs([]);
        setSparkColumnsDefs([]);
        setNewColumnDefs([...baseColumnDefs]);
      }

      // Restore scroll position after DOM updates
      try {
        const apiAfter = gridRef.current?.api;
        if (apiAfter) {
          if (mode === "append") {
            const indexToShow = firstVisibleRowRef.current ?? 0;
            setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
          } else if (mode === "prepend") {
            const indexToShow = (firstVisibleRowRef.current ?? 0) + (data?.length || 0);
            setTimeout(() => apiAfter.ensureIndexVisible?.(indexToShow, "top"), 50);
          }
        }
      } catch (err) {
        console.warn("Could not restore scroll for OnPremLowestCompliance", err);
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch assets data");
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
      isFetchingNext.current = false;
      isFetchingPrev.current = false;
    }
  }, [fetchAssetsCompliance]);

  // Throttled infinite scroll handler - FIXED: use ref for pagination to avoid recreating
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleScrollEnd = useCallback(() => {
    // SIMPLE FIX: Ignore the first scroll event (AG Grid auto-scroll on mount)
    scrollCount.current++;
    if (scrollCount.current === 1) {
      console.log("🚫 Ignored first scroll event (AG Grid auto-scroll)");
      return;
    }

    const now = Date.now();
    if (now - lastScrollTs.current < SCROLL_THROTTLE_MS) {
      return;
    }
    lastScrollTs.current = now;

    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }

    try {
      // Get the total row count from the data
      const totalRowCount = tableData.length;
      if (totalRowCount === 0) return;

      // Get viewport info from the event or API
      const verticalPixelRange = gridApi.getVerticalPixelRange?.();

      if (!verticalPixelRange) {
        // Fallback: use simple row count check
        const currentPagination = paginationRef.current;

        // If we have all data loaded and user scrolled, check if we need more
        if (currentPagination.hasNext && !isFetchingNext.current) {
          console.log("⬇️ Loading next page:", currentPagination.page + 1);
          isFetchingNext.current = true;
          fetchData({ page: currentPagination.page + 1, mode: "append" });
        }
        return;
      }

      // Calculate scroll position
      const { top, bottom } = verticalPixelRange;
      const scrollTop = top;
      const scrollHeight =
        gridApi.getModel?.()?.getRowCount?.() * 50 || totalRowCount * 50; // Assume 50px row height
      const clientHeight = bottom - top;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const isNearBottom = scrollPercentage > 0.8; // 80% scrolled
      const isNearTop = scrollTop < 100; // Less than 100px from top

      const currentPagination = paginationRef.current;

      console.log("📜 Scroll:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollPercentage: (scrollPercentage * 100).toFixed(1) + "%",
        isNearBottom,
        isNearTop,
        hasNext: currentPagination.hasNext,
        hasPrev: currentPagination.hasPrevious,
      });

      // Load next page when near bottom
      if (
        isNearBottom &&
        currentPagination.hasNext &&
        !isFetchingNext.current
      ) {
        console.log("⬇️ Loading next page:", currentPagination.page + 1);
        isFetchingNext.current = true;
        fetchData({ page: currentPagination.page + 1, mode: "append" });
        return;
      }

      // Load previous page when near top
      if (
        isNearTop &&
        currentPagination.hasPrevious &&
        !isFetchingPrev.current
      ) {
        console.log("⬆️ Loading previous page:", currentPagination.page - 1);
        isFetchingPrev.current = true;
        fetchData({ page: currentPagination.page - 1, mode: "prepend" });
        return;
      }
    } catch (err) {
      console.error("Scroll handler error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData.length]);

  const handleColumnSelection = (selectedNist: string, selectedCis: string, selectedPciDss: string, selectedSpark?: string) => {
    if (cisColumnsDefs.length > 0 || nistColumnsDefs.length > 0 || pciDssColumnsDefs.length > 0 || sparkColumnsDefs.length > 0) {
      setLoading(true);
      // Update the currently selected columns state
      setCurrentSelectedNist(selectedNist);
      setCurrentSelectedCis(selectedCis);
      setCurrentSelectedPciDss(selectedPciDss);
      if (selectedSpark) setCurrentSelectedSpark(selectedSpark);
      
      const singleNistColumn = nistColumnsDefs.find((column: any) => column.field === selectedNist);
      const singleCisColumn = cisColumnsDefs.find((column: any) => column.field === selectedCis);
      const singlePciDssColumn = pciDssColumnsDefs.find((column: any) => column.field === selectedPciDss);
      const singleSparkColumn = sparkColumnsDefs.find((column: any) => column.field === selectedSpark);
      const baseColumnDefs = isCloud ? cloudLowestComplianceColumnDefs : onPremLowestComplianceColumnDefs;
      const newColumnDefs = [
        ...baseColumnDefs,
        ...(singleNistColumn ? [singleNistColumn] : []),
        ...(singleCisColumn ? [singleCisColumn] : []),
        ...(singlePciDssColumn ? [singlePciDssColumn] : []),
        ...(singleSparkColumn ? [singleSparkColumn] : [])
      ];
      setNewColumnDefs(newColumnDefs);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const onRowClick = (data: any) => {
    dispatch(setSelectedRowID(data.id));
    setSelectedRow(data);
    setIsDrawerOpen(true);
  };

  const handleAssetComplianceReport = () => {
    setIsDrawerOpen(false);
    setReportDrawerOpen(true);
  };

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal",
      suppressScrollOnNewData: true,
      suppressColumnVirtualisation: true,
      suppressAnimationFrame: true,
      onBodyScrollEnd: handleScrollEnd,
      fullWidthCellRenderer: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ),
      isFullWidthRow: (params: any) => params.rowNode?.data?._isLoadingRow === true,
    }),
    [handleScrollEnd]
  );

  // Add loading row at the end when fetching more pages
  const displayData = useMemo(() => {
    if (isFetchingMore) {
      return [...tableData, { _isLoadingRow: true }];
    }
    return tableData;
  }, [tableData, isFetchingMore]);

  return (
    <div>
      <TableCard
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div>
              {isCloud ? "Resources With Compliance Risk" : "Assets With Compliance Risk"}
              </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>On-Prem</span>
              <AntSwitch
                checked={isCloud}
                onChange={(_, checked) => {
                  setIsCloud(checked);
                  setPagination({
                    page: 1,
                    totalPages: 1,
                    hasNext: false,
                    hasPrevious: false,
                  });
                }}
                disabled={switchDisabled}
               sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#FFFFFF",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#3A2ADA",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#3A2ADA",
                  },
                }}
              />
              <span>Cloud</span>
            </div>
          </div>
        }
        columnDefs={newColumnDefs}
        rowData={displayData}
        showSearch={false}
        isRowSelectable={false}
        loading={loading}
        onRowClick={(data) => {
          // Don't trigger row click for loading row
          if (!data._isLoadingRow) {
            onRowClick(data);
          }
        }}
        columDefOn={true}
        nistColumns={nistColumns}
        cisColumns={cisColumns}
        pciDssColumns={pciDssColumns}
        sparkColumns={sparkColumns}
        onColumnSelection={handleColumnSelection}
        currentSelectedNist={currentSelectedNist}
        currentSelectedCis={currentSelectedCis}
        currentSelectedPciDss={currentSelectedPciDss}
        currentSelectedSpark={currentSelectedSpark}
        showFilter={true}
        sortOptions={filterOptions}
        gridRef={gridRef}
        gridOptions={memoizedGridOptions}
      />
      {/* Loading indicator for pagination
      {isFetchingMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 1,
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )} */}
      <AssetDetailsDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        assetData={selectedRow}
        isCloud={isCloud}
        isCve={!isCloud}
                onReport={!isCloud ? handleAssetComplianceReport : undefined}

      />

       <Drawer
              anchor="right"
              open={reportDrawerOpen}
              onClose={() => {
                setIsDrawerOpen(true);
                setReportDrawerOpen(false);
              }}
              PaperProps={{ sx: { width: 400, borderRadius: 2 } }}
            >
              <AssetComplianceReportDrawer
                onClose={() => {
                  setIsDrawerOpen(true);
                  setReportDrawerOpen(false);
                }}
                assetData={selectedRow}
              />
            </Drawer>
    </div>
  );
};

export default OnPremLowestCompliance;
